<template>
  <div class="sea-calculation-page">
    <!-- 测算中遮罩 -->
    <div v-if="calculating" class="calculating-overlay">
      <div class="calculating-box">
        <p class="calculating-text">平台大数据正在进行测算分析中</p>
      </div>
    </div>

    <!-- 页面标题 -->
    <div class="page-header">
      <h1>企业出海测算</h1>
    </div>

    <!-- 核心内容区：仅在非结果页显示 -->
    <div class="main-content" v-if="!showResult">
      <div class="form-wrapper">
        <form class="calculation-form">
          <div class="form-item">
            <label class="form-label">公司名称</label>
            <input
              v-model="formData.companyName"
              class="form-input"
              placeholder="请输入公司名称"
              required
            />
          </div>

          <div class="form-item">
            <label class="form-label">联系人</label>
            <input
              v-model="formData.contactPerson"
              class="form-input"
              placeholder="请输入联系人姓名"
              required
            />
          </div>

          <div class="form-item">
            <label class="form-label">联系电话</label>
            <input
              v-model="formData.contactPhone"
              class="form-input"
              type="tel"
              placeholder="请输入11位手机号码"
              required
            />
          </div>

          <div class="form-item">
            <label class="form-label">出海国家/地区</label>
            <!-- 外层容器 -->
            <div class="search-select" ref="searchSelect">
              <!-- 输入框 -->
              <input
                v-model="keyword"
                class="form-input"
                placeholder="请选择国家/地区"
                @input="handleSearch"
                @focus="showDropdown = true"
                @blur="hideDropdownDelay"
                required
              />

              <!-- 下拉选项列表 -->
              <div
                v-show="showDropdown && filteredCountries.length > 0"
                class="dropdown-list"
              >
                <div
                  v-for="country in filteredCountries"
                  :key="country"
                  class="dropdown-item"
                  @mousedown="selectCountry(country)"
                >
                  {{ country }}
                </div>
              </div>
            </div>
          </div>

          <div
            class="form-item"
            v-for="(field, key) in formConfig.industry"
            :key="key"
          >
            <label class="form-label">{{ field.name }}</label>
            <select v-model="formData[field.key]" class="form-select" required>
              <option value="" disabled>{{ field.name }}</option>
              <option
                v-for="option in field.data"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>
          <div class="btn-group">
            <label class="agreement-row">
              <input
                v-model="agreementAccepted"
                type="checkbox"
                class="agreement-checkbox"
              />
              <span class="agreement-text">
                我已阅读并同意
                <a
                  :href="agreementUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="agreement-link"
                  @click.stop
                >
                  《用户协议》
                </a>
              </span>
            </label>
            <button
              type="button"
              class="calculate-btn"
              :class="{ 'calculate-btn--disabled': !agreementAccepted }"
              :disabled="!agreementAccepted"
              @click="handleDemandCalculate"
            >
              立即分析
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 测算结果展示页 -->
    <!-- 测算结果展示页 -->
    <div class="result-page" v-if="showResult">
      <div class="result-card">
        <div class="result-disclaimer-banner" role="status">
          <span class="result-disclaimer-tag">温馨提示</span>
          <p class="result-disclaimer-text">
            本次测算结果仅供参考，不构成任何操作建议。如需专业落地服务与定制化方案，可联系客服进行咨询。
          </p>
        </div>

        <div class="result-intro-block">
          <div class="card-title-destination font-weight-bold font-size-16">
            尊敬的用户【{{ formData.contactPerson }}】：
          </div>
          <div class="card-content-user">
            根据您选择的行业【{{ formData.industry }}】、企业规模【{{
              formData.scale
            }}】、出海目的【{{ formData.purpose }}】、出海预算【{{
              formData.budget
            }}】，经平台大数据与多方专业机构数据综合分析，我们为您提供的出海建议如下：
          </div>
        </div>

        <div class="report-wrapper">
          <!-- 标题区：左侧竖线 + 大标题 -->
          <div class="section-title-destination">
            <span class="title-line"></span>
            <h2>测算分析结果</h2>
          </div>

          <!-- 内容卡片区 -->
          <div class="report-grid">
            <!-- 综合分析 -->
            <div class="report-card full-card report-card-blue">
              <div class="card-title-destination">综合分析</div>
              <div class="card-content">{{ industryResultItem.result }}</div>
            </div>

            <!-- 测算提示 -->
            <div class="report-card report-card-blue">
              <div class="card-title-destination">推荐评分</div>
              <div class="card-content score-text font-blue font-weight-bold">
                {{ industryResultItem.score }}
              </div>
            </div>

            <!-- 市场机会 -->
            <div class="report-card report-card-blue">
              <div class="card-title-destination">出海策略</div>
              <div class="card-content font-blue font-weight-bold">
                {{ industryResultItem.advice }}
              </div>
            </div>

            <!-- 综合分析 -->
            <div class="report-card full-card report-card-green">
              <div class="card-title-destination font-green">推荐区域</div>
              <div class="card-content font-area-size">
                {{ industryResultItem.area }}
              </div>
            </div>

            <!-- 行业分析 -->
            <div class="report-card report-card-green">
              <div class="card-title-destination font-green">推荐国家</div>
              <div class="card-content font-weight-bold">
                {{ industryResultItem.countries }}
              </div>
            </div>

            <div class="report-card report-card-green">
              <div class="card-title-destination font-green">推荐原因</div>
              <div class="card-content">{{ industryResultItem.reason }}</div>
            </div>

            <!-- 规模建议 -->
            <div class="report-card report-card-blue">
              <div class="card-title-destination font-blue">潜力市场</div>
              <div class="card-content font-weight-bold">
                {{ industryResultItem.potential }}
              </div>
            </div>

            <div class="report-card report-card-blue">
              <div class="card-title-destination font-blue">潜力原因</div>
              <div class="card-content">
                {{ industryResultItem.potentialReason }}
              </div>
            </div>
            <!-- 目的导向 -->
            <div class="report-card">
              <div class="card-title-destination font-orange">风险市场</div>
              <div class="card-content font-weight-bold">
                {{ industryResultItem.risk }}
              </div>
            </div>
            <div class="report-card">
              <div class="card-title-destination font-orange">风险原因</div>
              <div class="card-content">
                {{ industryResultItem.riskReason }}
              </div>
            </div>
          </div>
        </div>

        <!-- 新增二维码区域（小程序来源不展示） -->
        <!-- <div v-if="sourceFrom !== 'xcx'" class="qrcode-section">
          <div class="qrcode-container">
            <div class="qrcode-item">
              <img class="qrcode-img" src="https://res.roadlinkglb.com/pic/mini/tiktok/kf.png" alt="客服人员二维码" />
              <p class="qrcode-desc">客服人员</p>
            </div>
            <div class="qrcode-item">
              <img class="qrcode-img" src="https://res.roadlinkglb.com/icon/code/zjsminicode.jpg" alt="小程序二维码" />
              <p class="qrcode-desc">小程序入口</p>
            </div>
          </div>
        </div> -->

        <!-- 返回重新分析按钮 -->

        <div class="result-disclaimer-banner" role="status">
          <span class="result-disclaimer-tag">温馨提示</span>
          <p class="result-disclaimer-text">
            本次测算基于基础维度进行初步分析，适用于企业出海方向的初步判断。如需结合更多维度进行深入评估，欢迎联系获取更精准的出海分析与路径建议。
          </p>
        </div>

        <!-- <div class="qrcode-section" v-if="isWeBankSource"> -->
        <div class="qrcode-section" v-if="isWeBankSource">
          <div class="qrcode-container">
            <div class="qrcode-item">
              <p style="color: #2444cb; font-size: 30px">
                如果您想了解更多出海信息，请扫描下方小程序二维码
              </p>
              <p>&nbsp;</p>
              <img
                class="qrcode-img"
                src="https://res.roadlinkglb.com/icon/code/zjsminicode.jpg"
                alt="小程序二维码"
              />
              <p class="qrcode-desc">政经事小程序</p>
            </div>
          </div>
        </div>

        <div class="btn-row">
          <button class="back-btn" @click="contactCustomerService">
            联系客服
          </button>
          <button class="back-btn" @click="backToForm">重新分析</button>
          <button class="back-btn" @click="jumpToWeekReport">
            查看出海月报
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SeaCalculation",
  data() {
    return {
      sourceFrom: "领航中心",
      // 测算中遮罩（点击测算后展示，2s 后出结果）
      calculating: false,
      // 是否显示结果页
      showResult: false,
      isWeBankSource: false, // 是否来自微众银行
      agreementAccepted: false,
      agreementUrl:
        "https://res.roadlinkglb.com/html/zhonghang/zhonghangxy.html",
      // 出海月报跳转地址（从远程 json 动态获取）
      weekReportUrl: "",
      // 表单数据存储
      formData: {
        companyName: "",
        contactPerson: "",
        contactPhone: "",
        industry: "",
        scale: "",
        purpose: "",
        budget: "",
        horizon: "",
        planCountry: "",
      },
      industryResult: null,
      industryResultItem: null,
      // 表单配置
      formConfig: {
        industry: {
          industry: {
            data: [
              "制造业",
              "人工智能",
              "新能源",
              "消费品",
              "服务业",
              "跨境电商",
              "数字科技",
              "医疗健康",
              "农业食品",
              "工程基建",
            ],
            name: "请选择行业",
            key: "industry",
          },
          scale: {
            data: [
              "初创/小微企业（100人以下）",
              "成长型企业（100–1000人）",
              "大型企业（1000人以上）",
            ],
            name: "请选择企业规模",
            key: "scale",
          },
          purpose: {
            data: ["市场拓展", "成本优化", "技术合作", "品牌国际化"],
            name: "请选择出海目的",
            key: "purpose",
          },
          budget: {
            data: ["50 万以下", "50–300 万", "300–1000 万", "1000 万以上"],
            name: "请选择出海预算",
            key: "budget",
          },
          horizon: {
            data: ["没有", "有海外客户", "有海外团队", "有海外公司"],
            name: "是否有出海经验",
            key: "horizon",
          },
        },
      },

      // 搜索关键词
      keyword: "",
      // 控制下拉显示
      showDropdown: false,
      // 全球170+国家列表（完整）
      allCountries: [
        // 亚洲
        "中国香港",
        "中国澳门",
        "中国台湾",
        "日本",
        "韩国",
        "朝鲜",
        "蒙古",
        "越南",
        "老挝",
        "柬埔寨",
        "泰国",
        "缅甸",
        "马来西亚",
        "新加坡",
        "文莱",
        "印度尼西亚",
        "菲律宾",
        "东帝汶",
        "尼泊尔",
        "不丹",
        "孟加拉国",
        "印度",
        "巴基斯坦",
        "斯里兰卡",
        "马尔代夫",
        "阿富汗",
        "伊拉克",
        "伊朗",
        "叙利亚",
        "约旦",
        "黎巴嫩",
        "以色列",
        "巴勒斯坦",
        "沙特阿拉伯",
        "巴林",
        "卡塔尔",
        "科威特",
        "阿联酋",
        "阿曼",
        "也门",
        "格鲁吉亚",
        "亚美尼亚",
        "阿塞拜疆",
        "土耳其",
        "塞浦路斯",
        "哈萨克斯坦",
        "吉尔吉斯斯坦",
        "塔吉克斯坦",
        "乌兹别克斯坦",
        "土库曼斯坦",

        // 欧洲
        "芬兰",
        "瑞典",
        "挪威",
        "冰岛",
        "丹麦",
        "俄罗斯",
        "乌克兰",
        "白俄罗斯",
        "摩尔多瓦",
        "波兰",
        "捷克",
        "斯洛伐克",
        "匈牙利",
        "德国",
        "奥地利",
        "瑞士",
        "列支敦士登",
        "英国",
        "爱尔兰",
        "荷兰",
        "比利时",
        "卢森堡",
        "法国",
        "摩纳哥",
        "西班牙",
        "葡萄牙",
        "安道尔",
        "意大利",
        "梵蒂冈",
        "圣马力诺",
        "马耳他",
        "斯洛文尼亚",
        "克罗地亚",
        "波黑",
        "塞尔维亚",
        "黑山",
        "北马其顿",
        "阿尔巴尼亚",
        "保加利亚",
        "罗马尼亚",
        "爱沙尼亚",
        "拉脱维亚",
        "立陶宛",
        "希腊",

        // 非洲
        "埃及",
        "利比亚",
        "突尼斯",
        "阿尔及利亚",
        "摩洛哥",
        "苏丹",
        "南苏丹",
        "埃塞俄比亚",
        "厄立特里亚",
        "索马里",
        "吉布提",
        "肯尼亚",
        "坦桑尼亚",
        "乌干达",
        "卢旺达",
        "布隆迪",
        "塞舌尔",
        "乍得",
        "中非",
        "喀麦隆",
        "赤道几内亚",
        "圣多美和普林西比",
        "尼日利亚",
        "贝宁",
        "多哥",
        "加纳",
        "科特迪瓦",
        "布基纳法索",
        "马里",
        "塞内加尔",
        "冈比亚",
        "几内亚",
        "几内亚比绍",
        "塞拉利昂",
        "利比里亚",
        "科摩罗",
        "马达加斯加",
        "毛里求斯",
        "津巴布韦",
        "博茨瓦纳",
        "纳米比亚",
        "南非",
        "莱索托",
        "斯威士兰",
        "赞比亚",
        "马拉维",
        "莫桑比克",
        "安哥拉",
        "刚果(金)",
        "刚果(布)",

        // 北美洲
        "加拿大",
        "美国",
        "墨西哥",
        "危地马拉",
        "伯利兹",
        "萨尔瓦多",
        "洪都拉斯",
        "尼加拉瓜",
        "哥斯达黎加",
        "巴拿马",
        "古巴",
        "海地",
        "多米尼加",
        "牙买加",
        "巴巴多斯",
        "特立尼达和多巴哥",
        "巴哈马",

        // 南美洲
        "哥伦比亚",
        "委内瑞拉",
        "圭亚那",
        "苏里南",
        "厄瓜多尔",
        "秘鲁",
        "巴西",
        "玻利维亚",
        "智利",
        "阿根廷",
        "乌拉圭",
        "巴拉圭",

        // 大洋洲
        "澳大利亚",
        "新西兰",
        "巴布亚新几内亚",
        "所罗门群岛",
        "瓦努阿图",
        "斐济",
        "萨摩亚",
        "汤加",
        "密克罗尼西亚",
        "马绍尔群岛",
        "帕劳",
        "瑙鲁",
        "基里巴斯",
      ],
      // 搜索后的结果
      filteredCountries: [],
    };
  },
  // 组件创建时自动执行
  created() {
    this.loadRemoteJson();
    this.loadWeekReportUrl();
    // 页面创建时获取URL参数
    this.getUrlParams();
  },
  methods: {
    // 解析URL参数的方法
    getUrlParams() {
      // 1. 获取当前页面的hash部分（因为是hash路由 #/xxx）
      const hash = window.location.hash;
      // 2. 截取?后面的参数部分
      const paramsStr = hash.split("?")[1] || "";
      if (!paramsStr) return; // 没有参数则直接返回

      // 3. 解析参数成键值对（仅按第一个 = 分割，避免值中含 =）
      const params = {};
      paramsStr.split("&").forEach((item) => {
        const idx = item.indexOf("=");
        if (idx === -1) return;
        const key = item.slice(0, idx);
        const raw = item.slice(idx + 1);
        if (!key) return;
        try {
          params[key] = decodeURIComponent(raw.replace(/\+/g, " "));
        } catch {
          params[key] = raw;
        }
      });
      // 4. sourceFrom
      if (params.sourceFrom) {
        this.sourceFrom = params.sourceFrom;
        // 如果是来自微众的，需要特殊处理展示小程序二维码
        // 判断 sourceFrom 是否包含 wz00 字符（不区分大小写）
        if (this.sourceFrom.includes("wz00")) {
          // 包含则标记为true，结果页展示小程序二维码
          this.isWeBankSource = true;
        }
      }
      // 5. 手机号、公司名、姓名（多别名兼容）
      const phone =
        params.phone || params.mobile || params.contactPhone || params.tel;
      const company =
        params.companyName || params.company || params.company_name;
      const personName =
        params.name ||
        params.contactPerson ||
        params.userName ||
        params.realName;
      if (phone) this.formData.contactPhone = phone;
      if (company) this.formData.companyName = company;
      if (personName) this.formData.contactPerson = personName;
    },
    async loadRemoteJson() {
      try {
        const response = await axios.get(
          "/res/config/mini/cesuan/result20260311.json"
        );
        this.industryResult = response.data.data;
        console.log("远程 industryResult 数据加载成功：", this.industryResult);
      } catch (err) {
        alert("远程数据加载失败，请刷新页面重试！");
        console.error("数据加载失败原因：", err);
      }
    },
    // 获取出海月报跳转地址（weekreport_url.json 中的 data 字段）
    async loadWeekReportUrl() {
      try {
        const response = await axios.get(
          "https://res.roadlinkglb.com/config/mini/weekreport_url.json"
        );
        if (response.data && response.data.data) {
          this.weekReportUrl = response.data.data;
          console.log("出海月报地址加载成功：", this.weekReportUrl);
        }
      } catch (err) {
        console.error("出海月报地址加载失败：", err);
      }
    },
    resetCurrentForm() {
      this.formData.companyName = "";
      this.formData.contactPerson = "";
      this.formData.contactPhone = "";
      this.formData.industry = "";
      this.formData.scale = "";
      this.formData.purpose = "";
      this.formData.budget = "";
      this.formData.horizon = "";
      this.formData.planCountry = "";
      this.agreementAccepted = false;
    },

    // 按需求出海测算
    async handleDemandCalculate() {
      if (!this.agreementAccepted) {
        alert("请先阅读并同意《用户协议》");
        return;
      }
      // 校验原行业表单字段（包含新增字段）
      if (!this.validateIndustryForm()) return;

      this.calculating = true;
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        console.log("【需求出海】表单数据：", this.formData);
        console.log("【需求出海】结果数据：", this.industryResult);

        // 映射 scale 为标准值
        this.formData.scale =
          this.formData.scale === "大型企业（1000人以上）"
            ? "大型企业"
            : [
                "初创/小微企业（100人以下）",
                "成长型企业（100–1000人）",
              ].includes(this.formData.scale)
            ? "中小企业"
            : "";

        this.industryResultItem = this.industryResult.find(
          (item) =>
            item.industry === this.formData.industry &&
            item.scale === this.formData.scale &&
            item.purpose === this.formData.purpose
        );

        console.log("【需求出海】提交数据：", {
          companyName: this.formData.companyName,
          contactPerson: this.formData.contactPerson,
          contactPhone: this.formData.contactPhone,
          industry: this.formData.industry,
          scale: this.formData.scale,
          purpose: this.formData.purpose,
          budget: this.formData.budget,
          horizon: this.formData.horizon,
          planCountry: this.formData.planCountry,
        });

        // 提交数据到后台，上报数据
        axios
          .post("/crm/crm/overseasCesuan/add", {
            companyName: this.formData.companyName,
            contactPerson: this.formData.contactPerson,
            contactPhone: this.formData.contactPhone,
            industry: this.formData.industry,
            scale: this.formData.scale,
            purpose: this.formData.purpose,
            countries: this.industryResultItem.countries,
            source: this.sourceFrom,
            planCountry: this.formData.planCountry,
          })
          .then((response) => {
            console.log("数据上报成功：", response.data);
          })
          .catch((error) => {
            console.error("数据上报失败：", error);
          });

        this.showResult = true;
      } finally {
        this.calculating = false;
      }
    },

    validateIndustryForm() {
      // 1. 校验新增字段非空
      if (!this.formData.companyName) {
        alert("请输入公司名称");
        return false;
      }
      if (!this.formData.contactPerson) {
        alert("请输入联系人");
        return false;
      }
      if (!this.formData.contactPhone) {
        alert("请输入联系电话");
        return false;
      }

      // 2. 校验手机号格式
      const phoneReg = /^1[3-9]\d{9}$/;
      if (!phoneReg.test(this.formData.contactPhone)) {
        alert("请输入正确的11位手机号码");
        return false;
      }

      // 3. 校验原有下拉字段
      const requiredFields = [
        "industry",
        "scale",
        "purpose",
        "budget",
        "horizon",
      ];
      for (const field of requiredFields) {
        if (!this.formData[field]) {
          alert(this.formConfig.industry[field].name);
          return false;
        }
      }
      return true;
    },
    /**
     * 联系客服
     * 1）必须用 wx.miniProgram.getEnv 判断是否真的在「小程序 web-view」内，不能只用 wx.miniProgram 是否存在。
     *    在微信内置浏览器里打开 H5 时，wx.miniProgram 仍可能存在，但 navigateBack/postMessage 无效，表现为点了没反应。
     * 2）非小程序环境：使用企业微信 kfid 网页链接。该链接主要面向微信/企业微信内打开；在普通手机浏览器或桌面浏览器中可能无法拉起会话或仅展示提示页，属平台能力限制，不是前端少写了跳转。
     * 文档：https://developers.weixin.qq.com/miniprogram/dev/component/web-view.html
     */
    contactCustomerService() {
      const kfUrl = "https://work.weixin.qq.com/kfid/kfcffeddfd4f46e8ba5";

      // 严格判断：是否在小程序 web-view 中
      const isMiniProgram = (() => {
        const ua = navigator.userAgent.toLowerCase();
        // 小程序 WebView 的 UA 特征
        return (
          (ua.includes("micromessenger") || ua.includes("wxwork")) &&
          (ua.includes("miniprogram") ||
            window.__wxjs_environment === "miniprogram")
        );
      })();

      console.log("是否真实小程序环境:", isMiniProgram);

      // 如果可能在小程序环境，异步验证
      if (isMiniProgram && window.wx && window.wx.miniProgram) {
        const mp = window.wx.miniProgram;

        // 设置超时，防止 getEnv 永不回调
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            console.log("getEnv 超时，判定为非小程序环境");
            this.openLink(kfUrl);
          }
        }, 500);

        mp.getEnv((res) => {
          resolved = true;
          clearTimeout(timeout);

          // 最终校验：getEnv 返回确实是小程序
          if (res && res.miniprogram === true) {
            console.log("确认是小程序环境:", res);
            mp.postMessage({
              data: { type: "openCustomerService" },
            });
            mp.navigateBack({
              delta: 1,
              fail: () => {
                mp.navigateTo({ url: "/pages/home_plus/index?pay=nopay" });
                console.warn("[contactCustomerService] navigateBack 失败");
              },
            });
          } else {
            // getEnv 返回不是小程序 → 走H5链接
            console.log("getEnv 确认非小程序环境:", res);
            this.openLink(kfUrl);
          }
        });

        return;
      }

      // 浏览器 / 非小程序环境
      console.log("非小程序环境，直接打开H5客服链接");
      this.openLink(kfUrl);
    },
    /** 浏览器内打开企业微信客服入口（需在微信内打开才能完整使用客服能力） */
    openLink(url) {
      console.log("openLink:", url);
      window.location.href = url;
    },
    // 返回表单页
    backToForm() {
      this.showResult = false;
      this.resetCurrentForm();
    },
    jumpToWeekReport() {
      // 优先使用远程动态获取的地址，兜底保留原地址
      const url =
        this.weekReportUrl ||
        "https://mp.weixin.qq.com/s/Gepm-FuC2uMEiZkUQpYhFQ";
      window.location.href = url;
    },
    // 搜索过滤
    handleSearch() {
      if (!this.keyword) {
        this.filteredCountries = this.allCountries;
        this.formData.planCountry = "";
        return;
      }
      this.filteredCountries = this.allCountries.filter((item) =>
        item.includes(this.keyword)
      );
    },
    // 选择国家
    selectCountry(country) {
      this.keyword = country;
      this.formData.planCountry = country; // 最终绑定值
      this.showDropdown = false;
    },
    // 延迟隐藏下拉（防止点击选项时提前关闭）
    hideDropdownDelay() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 150);
    },
  },
  mounted() {
    this.filteredCountries = this.allCountries;
  },
};
</script>

<style scoped>
/* 全局页面样式 */
.sea-calculation-page {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px 20px;
  font-family: "Microsoft YaHei", sans-serif;
  color: #333;
  background-color: #f9fafc;
}

/* 测算中全屏遮罩 */
.calculating-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}
.calculating-box {
  padding: 28px 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  text-align: center;
}
.calculating-text {
  margin: 0;
  font-size: 18px;
  color: #333;
  font-weight: 500;
}

/* 联系客服确认框（取消左、确认右） */
.customer-service-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
}
.customer-service-dialog {
  width: 100%;
  max-width: 340px;
  padding: 24px 22px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.customer-service-dialog-title {
  margin: 0 0 22px;
  font-size: 18px;
  line-height: 1.5;
  color: #333;
  text-align: center;
  font-weight: 500;
}
.customer-service-dialog-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 12px;
}
.customer-service-btn {
  flex: 1;
  min-height: 44px;
  padding: 0 16px;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: #fff;
  color: #333;
}
.customer-service-btn-cancel {
  flex: 1;
}
.customer-service-btn-confirm {
  flex: 1;
  border-color: #2444cb;
  background: #2444cb;
  color: #fff;
  font-weight: 500;
}

/* 页面标题 - 补充背景色 */
.page-header {
  text-align: center;
  margin-bottom: 10px;
  padding: 20px 20px;
  background-color: #2444cb;
  border-radius: 8px 8px 8px 8px;
}
.page-header h1 {
  font-size: 32px;
  color: white;
  font-weight: 600;
  margin: 0;
}

/* 核心内容区 */
.main-content {
  background: #fff;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* 表单样式 */
.form-wrapper {
  padding: 40px;
}
.calculation-form {
  width: 100%;
}
.form-item {
  margin-bottom: 24px;
}
.form-label {
  display: block;
  font-size: 16px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}
.form-select,
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  color: #333;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.3s;
}
.form-select:focus,
.form-input:focus {
  border-color: #2e86de;
  box-shadow: 0 0 0 2px rgba(46, 134, 222, 0.1);
}

/* 按钮组 */
.btn-group {
  text-align: center;
  margin-top: 32px;
}
.agreement-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  margin: 0 auto 16px;
  width: 90%;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  cursor: pointer;
  text-align: left;
}
.agreement-checkbox {
  flex-shrink: 0;
  margin-top: 3px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.agreement-text {
  flex: 1;
}
.agreement-link {
  color: #2444cb;
  text-decoration: none;
}
.agreement-link:hover {
  text-decoration: underline;
}
.calculate-btn {
  padding: 14px 48px;
  background: #2444cb;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s;
  width: 90%;
}
.calculate-btn--disabled,
.calculate-btn:disabled {
  background: #a8b4e8;
  cursor: not-allowed;
}

/* 结果页样式 */
.result-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
}
.result-card {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 28px 24px;
}

/* 结果页：说明区与「测算分析结果」之间的醒目提示 */
.result-intro-block {
  margin-bottom: 0;
}
.result-disclaimer-banner {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0px 0 24px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #fff9f0 0%, #fff5e6 100%);
  border: 1px solid #f0c78a;
  border-left: 5px solid #e6a23c;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.12);
}
.result-disclaimer-tag {
  display: inline-block;
  align-self: flex-start;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #b88230;
  background: rgba(230, 162, 60, 0.18);
  border-radius: 4px;
  letter-spacing: 0.02em;
}
.result-disclaimer-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.65;
  color: #4a3f2e;
  font-weight: 500;
}

/* 二维码区域 */
.qrcode-section {
  margin: 25px 0;
}
.qrcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
}
.qrcode-item {
  text-align: center;
}
.qrcode-img {
  width: 300px;
  height: 300px;
  object-fit: contain;
  border: 1px solid #eee;
  padding: 5px;
  border-radius: 8px;
}
.qrcode-desc {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

/* 按钮并排布局 */
.btn-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
.btn-row .back-btn {
  flex: 1;
  margin-top: 0;
  background-color: #2444cb;
}
.btn-row .back-btn:hover {
  background-color: #1a36a8;
}

/* 返回按钮基础样式 */
.back-btn {
  height: 44px;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

/** 新的样式：统一PC/移动端布局 + 颜色 */
.report-wrapper {
  font-family: "Microsoft YaHei", sans-serif;
  color: #333;
}

/* 标题区：左侧竖线 + 大标题 */
.report-wrapper .section-title-destination {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}
.section-title-destination .title-line {
  width: 4px;
  height: 36px;
  background-color: #2196f3;
  margin-right: 12px;
}
.section-title-destination h2 {
  font-size: 26px;
  font-weight: 700;
  color: #222;
  margin: 0;
}

/* 卡片网格布局：PC/移动端完全一致 */
.report-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  margin-bottom: 30px;
}
/* 综合分析卡片：独占一行 */
.full-card {
  grid-column: 1 / -1;
}

/* 基础卡片样式 */
.report-card {
  background-color: #fff8f4;
  border: 1px solid #f5e8dc;
  border-radius: 8px;
  padding: 18px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* 卡片标题 */
.card-title-destination {
  font-size: 16px;
  /* font-weight: 700; */
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  text-align: left;
  position: relative;
}
/* .card-title-destination::before {
  content: "";
  width: 3px;
  height: 18px;
  background-color: #409eff;
  border-radius: 2px;
  margin-right: 8px;
  flex-shrink: 0;
  margin-top: 3px;
} */

/* 卡片内容 */
.card-content {
  font-size: 15px;
  color: #333;
  line-height: 1.7;
  word-break: break-word;
}
/* 分数突出 */
.score-text {
  font-size: 24px;
  font-weight: bold;
  color: #d93030;
}

/* 强制移动端保持一样布局，不折叠 */
@media (max-width: 768px) {
  .sea-calculation-page {
    padding: 10px;
  }
  .page-header h1 {
    font-size: 16px;
  }
  .tab-item {
    font-size: 16px;
    padding: 12px 0;
  }
  .form-wrapper {
    padding: 20px;
  }
  .result-card {
    padding: 20px 15px;
  }

  /* 关键：移动端强制保持 1fr 2fr */
  .report-grid {
    grid-template-columns: 1fr 2fr !important;
    gap: 12px;
  }

  .section-title-destination h2 {
    font-size: 16px;
  }
  .section-title-destination .title-line {
    height: 28px;
  }
  .card-title-destination {
    font-size: 12px;
  }
  .card-content {
    font-size: 12px;
  }
  .score-text {
    font-size: 20px;
  }
}

.font-red {
  color: #d93030;
}

.font-green {
  color: #4caf50;
}

.font-blue {
  color: #2196f3;
}

.font-orange {
  color: #ff9800;
}

.font-area-size {
  font-size: 24px;
  font-weight: bold;
  display: flex;
  justify-content: center; /* 水平居中 */
  align-items: center; /* 垂直居中 */
  min-height: 60px; /* 保证卡片有最小高度，居中更明显 */
  text-align: center; /* 多行文字也居中 */
}

.report-card-green {
  background-color: #f7fffd;
  border-color: #c8e6c9;
}

.report-card-blue {
  background-color: #f7fbff;
  border-color: #bbdefb;
}

.card-content-user {
  font-size: 12px;
  color: #333;
  line-height: 20px; /* 上下文字5px间隔 */
  word-break: break-word;
  margin-bottom: 20px; /* 底部20px间隔 */
}

.font-weight-bold {
  font-weight: bold;
}

.font-size-16 {
  font-size: 16px;
}

/* ====================================== */
/* 👇 👇 👇 追加的平台页面样式 👇 👇 👇 */
/* ====================================== */
.platform-container {
  margin-top: 30px;
  width: 100%;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Roboto,
    sans-serif;
}

.status-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: #86909c;
  font-size: 14px;
}

.retry-btn {
  margin-top: 15px;
  padding: 8px 20px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 50px;
  cursor: pointer;
}

.header {
  margin-bottom: 20px;
  text-align: center;
}

.main-title {
  display: block;
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 8px;
}

.sub-title {
  display: inline-block;
  font-size: 14px;
  color: #2647c3;
  background: #f5f6fe;
  padding: 8px 14px;
}

.sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-card {
  padding-top: 20px;
}

.section-title {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #2647c3;
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 5px solid #2647c3;
}

.text-content {
  font-size: 15px;
  line-height: 1.6;
}

.array-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item {
  font-size: 15px;
}

.item-text {
  position: relative;
  padding-left: 16px;
}

.item-text::before {
  content: "•";
  position: absolute;
  left: 0;
}

.section-full-image {
  width: 100%;
  margin-top: 10px;
}

.member-card {
  margin-top: 30px;
  background: #f4f6ff;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  border: 1px solid #bcc2d8;
}

.member-title {
  font-size: 18px;
  font-weight: bold;
}

.discount {
  margin: 6px 0;
  background: #ca9123;
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  display: inline-block;
  font-size: 12px;
}

.price {
  font-size: 24px;
  font-weight: bold;
  color: #2343ca;
  margin-bottom: 10px;
}

.upgrade-btn {
  background: #2343ca;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 50px;
  font-size: 14px;
  cursor: pointer;
}
.form-item {
  margin: 16px 0;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.form-label {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  font-weight: 500;
}
.search-select {
  display: block;
  position: relative;
  width: 100%;
}

/* 输入框 */
.form-input {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
}
.form-input:focus {
  border-color: #409eff;
}

/* 下拉列表 */
.dropdown-list {
  position: absolute;
  top: 36px;
  left: 0;
  z-index: 10;
  width: 100%;
  max-height: 280px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

/* 下拉项 */
.dropdown-item {
  padding: 0 10px;
  height: 34px;
  line-height: 34px;
  cursor: pointer;
}
.dropdown-item:hover {
  background-color: #f5f7fa;
}
</style>